/**
 * Shared drawing helpers used by StudentPaperTake and StudentMockExamTake.
 * Extracted from inline definitions to prevent drift between the two files.
 */

/**
 * Compress a drawing data URL to JPEG at 0.78 quality, scaled to maxWidth.
 * Returns the original URL unchanged if it is not a data: image.
 */
export const compressDrawingForVerify = (dataUrl, maxWidth = 480) =>
  new Promise((resolve) => {
    if (!dataUrl || !dataUrl.startsWith('data:image/')) {
      resolve(dataUrl);
      return;
    }
    const img = new Image();
    img.onload = () => {
      const w = Math.min(img.width, maxWidth);
      const h = Math.round(img.height * (w / img.width));
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', 0.78));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

/**
 * Composite the student's stroke drawing on top of the question context snapshot.
 * Layout: strokes on top, 4 px grey separator, question context below.
 * Saved as JPEG 0.85 quality. Allows AI Vision to see both the answer and the question diagram.
 */
export const compositeWithContext = (strokesDataUrl, contextDataUrl, maxWidth = 800) =>
  new Promise((resolve) => {
    if (!strokesDataUrl || !contextDataUrl) {
      resolve(strokesDataUrl);
      return;
    }
    const ctxImg = new Image();
    const strokesImg = new Image();
    let loaded = 0;
    const onLoad = () => {
      loaded++;
      if (loaded < 2) return;
      const cw = Math.min(Math.max(ctxImg.width, strokesImg.width), maxWidth);
      const strokesH = Math.round(strokesImg.height * (cw / strokesImg.width));
      const ctxH = Math.round(ctxImg.height * (cw / ctxImg.width));
      const sepH = 4;
      const totalH = strokesH + sepH + ctxH;
      const c = document.createElement('canvas');
      c.width = cw;
      c.height = totalH;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, cw, totalH);
      ctx.drawImage(strokesImg, 0, 0, cw, strokesH);
      ctx.fillStyle = '#ccc';
      ctx.fillRect(0, strokesH, cw, sepH);
      ctx.drawImage(ctxImg, 0, strokesH + sepH, cw, ctxH);
      resolve(c.toDataURL('image/jpeg', 0.85));
    };
    ctxImg.onload = onLoad;
    strokesImg.onload = onLoad;
    ctxImg.onerror = () => resolve(strokesDataUrl);
    strokesImg.onerror = () => resolve(strokesDataUrl);
    ctxImg.src = contextDataUrl;
    strokesImg.src = strokesDataUrl;
  });

/**
 * Returns true when the question context snapshot should NOT be composited behind the
 * strokes image. Fill-type questions have number/word banks in the snapshot that confuse
 * AI Vision into misreading the typed answer.
 */
export const skipContextForDrawing = (question) => {
  if (!question) return false;
  const type = String(question.question_type || question.type || '').toLowerCase();
  if (type === 'fill-in-the-blank' || type === 'fill_in_the_blank' || type === 'multi-fill' || type === 'multi_fill' || type === 'table') return true;
  if (Array.isArray(question?.answer_data?.correct_blanks) && question.answer_data.correct_blanks.length > 0) return true;
  if (Array.isArray(question?.answer_data?.blank_labels) && question.answer_data.blank_labels.length > 0) return true;
  return false;
};

/**
 * Compress a TypeDraw {text, drawing} answer object before submit.
 * Does not composite with context (no per-question snapshots available at submit time).
 * Context compositing was already done when the student used Check Answer; the backend
 * will use the cached vision verdict from that call.
 */
export const compressAnswerForSubmit = async (val, maxWidth = 1200) => {
  if (val && typeof val === 'object' && val.drawing && String(val.drawing).startsWith('data:image/')) {
    const compressed = await compressDrawingForVerify(val.drawing, maxWidth);
    return { ...val, drawing: compressed };
  }
  return val;
};
